import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Mail } from "lucide-react";

const CONTACT_EMAIL = "fiestaman38470@gmail.com";
const LAST_UPDATED = "27 février 2026";

const Section = ({ marker, title, children, className = "" }) => (
  <section className={`border-b border-border ${className}`}>
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
      <div className="sux-marker mb-3">{marker}</div>
      <h2
        className="text-2xl md:text-3xl font-black tracking-tight mb-6 leading-tight"
        style={{ fontFamily: "Outfit" }}
      >
        {title}
      </h2>
      <div className="text-[15px] leading-relaxed space-y-4 text-foreground/90">
        {children}
      </div>
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 leading-relaxed">
    <span className="w-1.5 h-1.5 bg-[#002FA7] mt-2.5 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

export default function Privacy() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isFr = i18n.resolvedLanguage === "fr";

  const txt = isFr
    ? {
        page_title: "Politique de confidentialité",
        page_subtitle:
          "Cette politique décrit comment SUX1 collecte, utilise et protège vos données personnelles, conformément au RGPD et aux exigences du Google Play Store.",
        last_updated: `Dernière mise à jour : ${LAST_UPDATED}`,
        s1_marker: "01 · QUI SOMMES-NOUS",
        s1_title: "L'éditeur de SUX1",
        s1_body: (
          <>
            <p>
              SUX1 est une application web et mobile (Progressive Web App / Android) qui permet
              de résumer rapidement n'importe quel texte à l'aide de modèles d'intelligence
              artificielle.
            </p>
            <p>
              L'éditeur du service est un développeur indépendant agissant en qualité de
              responsable du traitement au sens du RGPD. Pour toute question relative à vos
              données personnelles, vous pouvez nous contacter à l'adresse :
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-email" className="font-mono text-sm bg-secondary px-4 py-3 inline-block text-[#002FA7] underline underline-offset-2 hover:bg-[#002FA7]/10 transition-colors">
              {CONTACT_EMAIL}
            </a>
          </>
        ),
        s2_marker: "02 · DONNÉES COLLECTÉES",
        s2_title: "Ce que nous collectons (et rien de plus)",
        s2_body: (
          <>
            <p>Quand vous utilisez SUX1, nous collectons uniquement :</p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Adresse e-mail</strong> : utilisée comme identifiant de compte et pour
                la connexion.
              </Bullet>
              <Bullet>
                <strong>Mot de passe</strong> : stocké sous forme hachée (bcrypt) — jamais en
                clair, jamais accessible par l'équipe.
              </Bullet>
              <Bullet>
                <strong>Nom d'affichage</strong> : optionnel, fourni à l'inscription.
              </Bullet>
              <Bullet>
                <strong>Textes que vous soumettez pour résumé</strong> : conservés associés à
                votre compte pour que vous puissiez retrouver l'historique de vos résumés.
              </Bullet>
              <Bullet>
                <strong>Résumés générés</strong> : produits par les modèles d'IA et liés à votre
                compte.
              </Bullet>
              <Bullet>
                <strong>Métadonnées techniques minimales</strong> : modèle utilisé, longueur,
                format, date de génération, langue choisie.
              </Bullet>
              <Bullet>
                <strong>Données de facturation Stripe</strong> (pour les utilisateurs Pro) :
                identifiant de session Stripe et statut du paiement. Nous ne voyons ni ne
                stockons jamais le numéro de carte bancaire.
              </Bullet>
            </ul>
            <p>
              <strong>Ce que nous ne collectons PAS :</strong> aucune adresse IP stockée, aucune
              donnée de géolocalisation, aucun cookie publicitaire, aucun tracker tiers, aucune
              donnée comportementale, aucun accès au répertoire ou aux contacts du téléphone.
            </p>
          </>
        ),
        s3_marker: "03 · FINALITÉS",
        s3_title: "Pourquoi nous traitons vos données",
        s3_body: (
          <>
            <ul className="space-y-2">
              <Bullet>
                <strong>Authentification</strong> : vous permettre de vous connecter à votre
                compte SUX1.
              </Bullet>
              <Bullet>
                <strong>Génération de résumés</strong> : envoyer le texte que vous fournissez
                à un modèle d'IA tiers (voir section 4) et stocker le résumé produit.
              </Bullet>
              <Bullet>
                <strong>Historique personnel</strong> : conserver vos résumés pour que vous
                puissiez les retrouver.
              </Bullet>
              <Bullet>
                <strong>Paiement Pro</strong> : encaisser les achats de packs de crédits via Stripe
                et activer le statut Pro de votre compte.
              </Bullet>
              <Bullet>
                <strong>Sécurité</strong> : prévenir les abus et les fraudes (rate-limiting de
                base).
              </Bullet>
            </ul>
            <p>
              <strong>Base légale</strong> : l'exécution du contrat (article 6.1.b du RGPD) pour
              le service de résumé et la facturation, et votre consentement (article 6.1.a) pour
              la création de compte.
            </p>
          </>
        ),
        s4_marker: "04 · SOUS-TRAITANTS IA",
        s4_title: "Les modèles d'IA tiers",
        s4_body: (
          <>
            <p>
              Pour générer vos résumés, SUX1 transmet le texte que vous fournissez à l'un des
              trois fournisseurs d'IA suivants, selon le modèle que vous sélectionnez :
            </p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Anthropic</strong> (Claude Sonnet 4.5) — serveurs aux États-Unis.
                <br />
                <a
                  href="https://www.anthropic.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Politique Anthropic
                </a>
              </Bullet>
              <Bullet>
                <strong>OpenAI</strong> (GPT-5.2) — serveurs aux États-Unis.
                <br />
                <a
                  href="https://openai.com/policies/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Politique OpenAI
                </a>
              </Bullet>
              <Bullet>
                <strong>Google DeepMind</strong> (Gemini 3 Flash) — serveurs aux États-Unis et
                Union européenne.
                <br />
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Politique Google
                </a>
              </Bullet>
            </ul>
            <p>
              <strong>Engagement contractuel :</strong> ces fournisseurs s'engagent
              contractuellement à <strong>ne pas utiliser vos textes pour entraîner leurs
              modèles d'IA</strong> dans le cadre des appels API que SUX1 effectue (politique
              "no training" par défaut sur les APIs Claude, OpenAI et Gemini).
            </p>
            <p>
              Le transfert hors UE est encadré par les clauses contractuelles types (SCC) de la
              Commission européenne.
            </p>
          </>
        ),
        s5_marker: "05 · AUTRES SOUS-TRAITANTS",
        s5_title: "Hébergement, base de données et paiement",
        s5_body: (
          <>
            <ul className="space-y-2">
              <Bullet>
                <strong>Hébergement</strong> : Emergent (États-Unis / Union européenne) —
                serveurs applicatifs et stockage des fichiers temporaires (PDF/DOCX en transit).
              </Bullet>
              <Bullet>
                <strong>Base de données</strong> : MongoDB Atlas — stockage des comptes,
                résumés et métadonnées (région Europe quand disponible).
              </Bullet>
              <Bullet>
                <strong>Paiement</strong> : Stripe Payments Europe (Irlande). Les données de
                carte bancaire sont collectées et traitées <strong>exclusivement par
                Stripe</strong>, conformément à la norme PCI-DSS. SUX1 ne reçoit jamais le
                numéro de carte.
                <br />
                <a
                  href="https://stripe.com/fr/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Politique Stripe
                </a>
              </Bullet>
            </ul>
          </>
        ),
        s6_marker: "06 · DURÉE DE CONSERVATION",
        s6_title: "Combien de temps nous gardons vos données",
        s6_body: (
          <>
            <ul className="space-y-2">
              <Bullet>
                <strong>Compte utilisateur</strong> : conservé tant que votre compte est actif.
                Vous pouvez le supprimer à tout moment depuis la page Paramètres ou en nous
                écrivant.
              </Bullet>
              <Bullet>
                <strong>Résumés et textes originaux</strong> : conservés tant que votre compte
                est actif. Supprimables individuellement depuis l'Historique.
              </Bullet>
              <Bullet>
                <strong>Données de facturation</strong> : conservées 10 ans pour respecter les
                obligations comptables et fiscales françaises.
              </Bullet>
              <Bullet>
                <strong>Comptes inactifs</strong> : un compte inactif pendant plus de 3 ans
                pourra être supprimé après notification par e-mail.
              </Bullet>
            </ul>
          </>
        ),
        s7_marker: "07 · VOS DROITS",
        s7_title: "Vos droits RGPD",
        s7_body: (
          <>
            <p>
              En tant que personne concernée, vous disposez à tout moment des droits suivants :
            </p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Droit d'accès</strong> : obtenir une copie de toutes les données que
                nous avons sur vous.
              </Bullet>
              <Bullet>
                <strong>Droit de rectification</strong> : corriger une donnée inexacte.
              </Bullet>
              <Bullet>
                <strong>Droit à l'effacement</strong> : demander la suppression de votre compte
                et de toutes vos données.
              </Bullet>
              <Bullet>
                <strong>Droit d'opposition</strong> : vous opposer au traitement.
              </Bullet>
              <Bullet>
                <strong>Droit à la portabilité</strong> : récupérer vos données dans un format
                exploitable (JSON).
              </Bullet>
              <Bullet>
                <strong>Droit de réclamation</strong> : saisir la CNIL (
                <a
                  href="https://www.cnil.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline"
                >
                  www.cnil.fr
                </a>
                ) si vous estimez que vos droits ne sont pas respectés.
              </Bullet>
            </ul>
            <p>
              Pour exercer ces droits, écrivez-nous à{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[#002FA7] underline underline-offset-2">{CONTACT_EMAIL}</a>. Nous répondrons
              dans un délai maximum de 30 jours.
            </p>
          </>
        ),
        s8_marker: "08 · SÉCURITÉ",
        s8_title: "Comment nous protégeons vos données",
        s8_body: (
          <>
            <ul className="space-y-2">
              <Bullet>Connexion HTTPS chiffrée sur tout le site (TLS 1.3).</Bullet>
              <Bullet>Mots de passe hachés avec bcrypt — jamais en clair.</Bullet>
              <Bullet>
                Jetons d'authentification JWT signés et à durée limitée.
              </Bullet>
              <Bullet>
                Aucun accès direct à la base de données depuis l'extérieur — uniquement via API
                authentifiée.
              </Bullet>
              <Bullet>
                Sauvegardes chiffrées de la base de données.
              </Bullet>
              <Bullet>
                Mise à jour régulière des dépendances logicielles.
              </Bullet>
            </ul>
          </>
        ),
        s9_marker: "09 · ENFANTS",
        s9_title: "Utilisation par les mineurs",
        s9_body: (
          <>
            <p>
              SUX1 n'est pas destiné aux enfants de moins de 13 ans. Si vous êtes parent ou
              tuteur légal et constatez que votre enfant a créé un compte sans votre
              consentement, contactez-nous à{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[#002FA7] underline underline-offset-2">{CONTACT_EMAIL}</a> et nous
              supprimerons immédiatement le compte et les données associées.
            </p>
          </>
        ),
        s10_marker: "10 · MODIFICATIONS",
        s10_title: "Évolution de cette politique",
        s10_body: (
          <>
            <p>
              Cette politique de confidentialité peut être mise à jour pour refléter des
              changements légaux, techniques ou fonctionnels. Toute modification significative
              vous sera notifiée par e-mail à l'adresse associée à votre compte au moins 30
              jours avant la prise d'effet.
            </p>
            <p>
              La version actuellement en vigueur est celle datée du{" "}
              <strong>{LAST_UPDATED}</strong>.
            </p>
          </>
        ),
        s11_marker: "11 · SCANNER UN LIVRE",
        s11_title: "Feature « Scanner un livre » et droit d'auteur",
        s11_body: (
          <>
            <p>
              La feature <strong>« Scanner un livre »</strong> permet de photographier la
              couverture (et éventuellement la 4e de couverture) d'un livre pour générer une
              fiche de lecture par IA.
            </p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Aucun stockage d'image</strong> : les photos sont traitées en mémoire
                puis supprimées immédiatement. Elles ne sont jamais persistées sur nos serveurs.
              </Bullet>
              <Bullet>
                <strong>Résumé transformatif</strong> : la fiche est générée à partir des
                connaissances publiques du modèle d'IA sur l'ouvrage. Elle ne reproduit pas le
                texte du livre et respecte le droit d'auteur (art. L122-5 CPI et directive
                européenne 2019/790).
              </Bullet>
              <Bullet>
                <strong>Aucune citation étendue</strong> : le modèle est instruit de ne pas
                reproduire plus de 30 mots consécutifs du texte original.
              </Bullet>
              <Bullet>
                <strong>Engagement de l'utilisateur</strong> : en soumettant une photo,{" "}
                <strong>vous déclarez avoir le droit de photographier l'ouvrage soumis</strong>{" "}
                (livre vous appartenant, exemplaire consulté légalement, ou usage privé). Vous
                êtes seul responsable du respect des droits attachés aux photos que vous
                envoyez.
              </Bullet>
              <Bullet>
                <strong>Usage privé recommandé</strong> : la fiche générée est destinée à votre
                usage personnel (études, curiosité, aide-mémoire). Sa republication publique
                sans mention de la source relève de votre responsabilité.
              </Bullet>
            </ul>
            <p>
              Si vous êtes ayant droit d'un ouvrage et que vous souhaitez qu'il ne soit plus
              analysable par SUX1, écrivez-nous à{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[#002FA7] underline underline-offset-2">{CONTACT_EMAIL}</a>.
            </p>
          </>
        ),
        contact_cta:
          "Une question sur vos données ? Nous répondons à toute demande dans les 30 jours.",
        contact_btn: "Nous contacter",
        footer_back: "Retour à l'accueil",
      }
    : {
        page_title: "Privacy Policy",
        page_subtitle:
          "This policy describes how SUX1 collects, uses, and protects your personal data, in compliance with the GDPR and Google Play Store requirements.",
        last_updated: `Last updated: ${LAST_UPDATED}`,
        s1_marker: "01 · WHO WE ARE",
        s1_title: "The publisher of SUX1",
        s1_body: (
          <>
            <p>
              SUX1 is a web and mobile (PWA / Android) application that allows users to quickly
              summarize any text using AI models.
            </p>
            <p>
              The service is operated by an independent developer acting as the data controller
              under the GDPR. For any question about your personal data, you can contact us at:
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="privacy-contact-email" className="font-mono text-sm bg-secondary px-4 py-3 inline-block text-[#002FA7] underline underline-offset-2 hover:bg-[#002FA7]/10 transition-colors">
              {CONTACT_EMAIL}
            </a>
          </>
        ),
        s2_marker: "02 · DATA WE COLLECT",
        s2_title: "What we collect (and nothing more)",
        s2_body: (
          <>
            <p>When you use SUX1, we only collect:</p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Email address</strong>: used as account identifier and for login.
              </Bullet>
              <Bullet>
                <strong>Password</strong>: stored hashed (bcrypt) — never in plain text, never
                accessible to the team.
              </Bullet>
              <Bullet>
                <strong>Display name</strong>: optional, provided at signup.
              </Bullet>
              <Bullet>
                <strong>Texts you submit for summarization</strong>: stored linked to your
                account so you can access your summary history.
              </Bullet>
              <Bullet>
                <strong>Generated summaries</strong>: produced by AI models and linked to your
                account.
              </Bullet>
              <Bullet>
                <strong>Minimal technical metadata</strong>: model used, length, format, date,
                chosen language.
              </Bullet>
              <Bullet>
                <strong>Stripe billing data</strong> (for Pro users): Stripe session ID and
                payment status. We never see or store credit card numbers.
              </Bullet>
            </ul>
            <p>
              <strong>What we do NOT collect:</strong> no stored IP addresses, no geolocation,
              no advertising cookies, no third-party trackers, no behavioral data, no access to
              your phone's contacts or photo library.
            </p>
          </>
        ),
        s3_marker: "03 · PURPOSE",
        s3_title: "Why we process your data",
        s3_body: (
          <>
            <ul className="space-y-2">
              <Bullet>
                <strong>Authentication</strong>: to let you log in to your SUX1 account.
              </Bullet>
              <Bullet>
                <strong>Summary generation</strong>: to send your text to a third-party AI
                model (see section 4) and store the resulting summary.
              </Bullet>
              <Bullet>
                <strong>Personal history</strong>: to let you find your past summaries.
              </Bullet>
              <Bullet>
                <strong>Pro payment</strong>: to process credit-pack purchases via Stripe
                and activate your Pro status.
              </Bullet>
              <Bullet>
                <strong>Security</strong>: to prevent abuse and fraud (basic rate-limiting).
              </Bullet>
            </ul>
            <p>
              <strong>Legal basis</strong>: contract performance (GDPR art. 6.1.b) for the
              summarization service and billing, and your consent (art. 6.1.a) for account
              creation.
            </p>
          </>
        ),
        s4_marker: "04 · AI SUBPROCESSORS",
        s4_title: "Third-party AI models",
        s4_body: (
          <>
            <p>
              To generate your summaries, SUX1 forwards the text you provide to one of the
              following three AI providers, depending on the model you select:
            </p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Anthropic</strong> (Claude Sonnet 4.5) — US servers.
                <br />
                <a
                  href="https://www.anthropic.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Anthropic policy
                </a>
              </Bullet>
              <Bullet>
                <strong>OpenAI</strong> (GPT-5.2) — US servers.
                <br />
                <a
                  href="https://openai.com/policies/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  OpenAI policy
                </a>
              </Bullet>
              <Bullet>
                <strong>Google DeepMind</strong> (Gemini 3 Flash) — US and EU servers.
                <br />
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Google policy
                </a>
              </Bullet>
            </ul>
            <p>
              <strong>Contractual commitment:</strong> these providers contractually agree{" "}
              <strong>not to use your texts to train their AI models</strong> in the context of
              the API calls SUX1 makes (default "no training" policy on the Claude, OpenAI and
              Gemini APIs).
            </p>
            <p>
              Transfers outside the EU are governed by the European Commission's Standard
              Contractual Clauses (SCC).
            </p>
          </>
        ),
        s5_marker: "05 · OTHER SUBPROCESSORS",
        s5_title: "Hosting, database, and payment",
        s5_body: (
          <>
            <ul className="space-y-2">
              <Bullet>
                <strong>Hosting</strong>: Emergent (US / EU) — application servers and
                temporary file storage (PDFs/DOCX in transit).
              </Bullet>
              <Bullet>
                <strong>Database</strong>: MongoDB Atlas — stores accounts, summaries, and
                metadata (EU region when available).
              </Bullet>
              <Bullet>
                <strong>Payment</strong>: Stripe Payments Europe (Ireland). Credit card data is
                collected and processed <strong>exclusively by Stripe</strong>, in compliance
                with PCI-DSS. SUX1 never receives card numbers.
                <br />
                <a
                  href="https://stripe.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline text-sm"
                >
                  Stripe policy
                </a>
              </Bullet>
            </ul>
          </>
        ),
        s6_marker: "06 · RETENTION",
        s6_title: "How long we keep your data",
        s6_body: (
          <>
            <ul className="space-y-2">
              <Bullet>
                <strong>User account</strong>: kept as long as your account is active. You can
                delete it at any time from Settings or by writing to us.
              </Bullet>
              <Bullet>
                <strong>Summaries and original texts</strong>: kept as long as your account is
                active. Individually deletable from History.
              </Bullet>
              <Bullet>
                <strong>Billing data</strong>: kept for 10 years to comply with French
                accounting and tax obligations.
              </Bullet>
              <Bullet>
                <strong>Inactive accounts</strong>: an account inactive for more than 3 years
                may be deleted after email notification.
              </Bullet>
            </ul>
          </>
        ),
        s7_marker: "07 · YOUR RIGHTS",
        s7_title: "Your GDPR rights",
        s7_body: (
          <>
            <p>As a data subject, you have the following rights at any time:</p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>Right of access</strong>: obtain a copy of all the data we hold about
                you.
              </Bullet>
              <Bullet>
                <strong>Right to rectification</strong>: correct inaccurate data.
              </Bullet>
              <Bullet>
                <strong>Right to erasure</strong>: request the deletion of your account and all
                associated data.
              </Bullet>
              <Bullet>
                <strong>Right to object</strong>: object to processing.
              </Bullet>
              <Bullet>
                <strong>Right to portability</strong>: retrieve your data in a usable format
                (JSON).
              </Bullet>
              <Bullet>
                <strong>Right to lodge a complaint</strong>: contact the French CNIL (
                <a
                  href="https://www.cnil.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] underline"
                >
                  www.cnil.fr
                </a>
                ) or your local data protection authority.
              </Bullet>
            </ul>
            <p>
              To exercise these rights, write to us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[#002FA7] underline underline-offset-2">{CONTACT_EMAIL}</a>. We will reply
              within 30 days at most.
            </p>
          </>
        ),
        s8_marker: "08 · SECURITY",
        s8_title: "How we protect your data",
        s8_body: (
          <>
            <ul className="space-y-2">
              <Bullet>HTTPS encryption across the entire site (TLS 1.3).</Bullet>
              <Bullet>Passwords hashed with bcrypt — never plain text.</Bullet>
              <Bullet>JWT authentication tokens signed and time-limited.</Bullet>
              <Bullet>
                No direct database access from outside — only via authenticated API.
              </Bullet>
              <Bullet>Encrypted database backups.</Bullet>
              <Bullet>Regular dependency updates.</Bullet>
            </ul>
          </>
        ),
        s9_marker: "09 · CHILDREN",
        s9_title: "Use by minors",
        s9_body: (
          <>
            <p>
              SUX1 is not intended for children under 13. If you are a parent or legal guardian
              and discover that your child has created an account without your consent, contact
              us at <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[#002FA7] underline underline-offset-2">{CONTACT_EMAIL}</a> and we
              will immediately delete the account and associated data.
            </p>
          </>
        ),
        s10_marker: "10 · UPDATES",
        s10_title: "Changes to this policy",
        s10_body: (
          <>
            <p>
              This privacy policy may be updated to reflect legal, technical, or functional
              changes. Any significant change will be notified to you by email to the address
              associated with your account at least 30 days before taking effect.
            </p>
            <p>
              The version currently in force is the one dated <strong>{LAST_UPDATED}</strong>.
            </p>
          </>
        ),
        s11_marker: "11 · BOOK SCAN",
        s11_title: "Book scan feature and copyright",
        s11_body: (
          <>
            <p>
              The <strong>&quot;Scan a book&quot;</strong> feature lets you photograph the front
              cover (and optionally the back cover) of a book to generate an AI-powered reading
              sheet.
            </p>
            <ul className="space-y-2 my-4">
              <Bullet>
                <strong>No image storage</strong>: photos are processed in memory and deleted
                immediately. They are never persisted on our servers.
              </Bullet>
              <Bullet>
                <strong>Transformative summary</strong>: the sheet is generated from the AI
                model&apos;s public knowledge of the work. It does not reproduce the book&apos;s
                text and complies with copyright law (French CPI art. L122-5 and EU Directive
                2019/790).
              </Bullet>
              <Bullet>
                <strong>No extended quotations</strong>: the model is instructed not to
                reproduce more than 30 consecutive words from the original text.
              </Bullet>
              <Bullet>
                <strong>User commitment</strong>: by submitting a photo,{" "}
                <strong>you declare that you have the right to photograph the submitted
                work</strong> (book you own, legally consulted copy, or private use). You are
                solely responsible for respecting the rights attached to the photos you send.
              </Bullet>
              <Bullet>
                <strong>Private use recommended</strong>: the generated sheet is intended for
                your personal use (study, curiosity, aide-mémoire). Public re-publication
                without source citation is your responsibility.
              </Bullet>
            </ul>
            <p>
              If you are the rights holder of a work and wish it to be no longer analysable by
              SUX1, write to us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-mono text-[#002FA7] underline underline-offset-2">{CONTACT_EMAIL}</a>.
            </p>
          </>
        ),
        contact_cta:
          "Any question about your data? We answer every request within 30 days.",
        contact_btn: "Contact us",
        footer_back: "Back to home",
      };

  return (
    <div className="min-h-screen bg-card">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="h-16 max-w-5xl mx-auto flex items-center px-6 md:px-10 justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-home-link">
            <span className="inline-block w-7 h-7 bg-[#002FA7]" />
            <span
              className="font-black text-2xl tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              SUX<span className="text-[#FF2A00]">1</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to={user ? "/" : "/login"}
              className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors"
              data-testid="privacy-nav-cta"
            >
              {user ? t("nav.summarize") : t("nav.signin")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-b from-secondary/30 to-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">{txt.last_updated}</div>
          <h1
            className="text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-5"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.page_title}
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
            {txt.page_subtitle}
          </p>
        </div>
      </section>

      <Section marker={txt.s1_marker} title={txt.s1_title}>{txt.s1_body}</Section>
      <Section marker={txt.s2_marker} title={txt.s2_title}>{txt.s2_body}</Section>
      <Section marker={txt.s3_marker} title={txt.s3_title}>{txt.s3_body}</Section>
      <Section marker={txt.s4_marker} title={txt.s4_title}>{txt.s4_body}</Section>
      <Section marker={txt.s5_marker} title={txt.s5_title}>{txt.s5_body}</Section>
      <Section marker={txt.s6_marker} title={txt.s6_title}>{txt.s6_body}</Section>
      <Section marker={txt.s7_marker} title={txt.s7_title}>{txt.s7_body}</Section>
      <Section marker={txt.s8_marker} title={txt.s8_title}>{txt.s8_body}</Section>
      <Section marker={txt.s9_marker} title={txt.s9_title}>{txt.s9_body}</Section>
      <Section marker={txt.s10_marker} title={txt.s10_title}>{txt.s10_body}</Section>
      <Section marker={txt.s11_marker} title={txt.s11_title}>{txt.s11_body}</Section>

      {/* Contact CTA */}
      <section className="border-b border-border bg-[#002FA7] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-14 text-center">
          <p className="text-base mb-6 leading-relaxed text-white/90">{txt.contact_cta}</p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="privacy-contact-mailto"
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            {txt.contact_btn} · {CONTACT_EMAIL}
          </a>
        </div>
      </section>

      <footer className="py-10 px-6 md:px-10 text-center">
        <Link
          to="/"
          data-testid="privacy-footer-home"
          className="sux-marker hover:text-[#002FA7] transition-colors"
        >
          ← {txt.footer_back}
        </Link>
      </footer>
    </div>
  );
}
